if (!customElements.get('tmw-hero')) {
  class TmwHero extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.timer = null;
      this.isVisible = false;
      this.touchStartX = null;
      this.manuallyPaused = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileMedia = window.matchMedia('(max-width: 749px)');
      this.handleVisibility = this.handleVisibility.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.toggleAutoplay = this.toggleAutoplay.bind(this);
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-tmw-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-tmw-dot]'));
      this.autoplayButton = this.querySelector('[data-tmw-autoplay]');
      if (!this.slides.length) return;

      this.querySelector('[data-tmw-previous]')?.addEventListener('click', () => this.showPrevious(true));
      this.querySelector('[data-tmw-next]')?.addEventListener('click', () => this.showNext(true));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.showSlide(Number(dot.dataset.tmwDot), true)));
      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.addEventListener('touchend', this.handleTouchEnd, { passive: true });
      this.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.autoplayButton?.addEventListener('click', this.toggleAutoplay);
      this.reducedMotion.addEventListener('change', this.handleMotionChange);
      this.mobileMedia.addEventListener('change', this.handleViewportChange);

      this.observer = new IntersectionObserver(this.handleVisibility, { threshold: 0.15 });
      this.observer.observe(this);
      this.showSlide(0, false);
    }

    disconnectedCallback() {
      this.stopTimer();
      this.pauseAllVideos();
      this.observer?.disconnect();
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('touchstart', this.handleTouchStart);
      this.removeEventListener('touchend', this.handleTouchEnd);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.autoplayButton?.removeEventListener('click', this.toggleAutoplay);
      this.reducedMotion.removeEventListener('change', this.handleMotionChange);
      this.mobileMedia.removeEventListener('change', this.handleViewportChange);
    }

    handleVisibility(entries) {
      this.isVisible = entries[0].isIntersecting;
      if (this.isVisible) {
        this.startActiveMedia();
      } else {
        this.stopTimer();
        this.pauseAllVideos();
      }
    }

    handleMotionChange() {
      this.stopTimer();
      this.pauseAllVideos();
      if (this.isVisible) this.startActiveMedia();
    }

    handleViewportChange() {
      this.pauseAllVideos();
      if (this.isVisible) this.startActiveMedia();
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') this.showPrevious(true);
      if (event.key === 'ArrowRight') this.showNext(true);
    }

    handleTouchStart(event) {
      this.touchStartX = event.changedTouches[0].clientX;
    }

    handleTouchEnd(event) {
      if (this.touchStartX === null) return;
      const distance = event.changedTouches[0].clientX - this.touchStartX;
      this.touchStartX = null;
      if (Math.abs(distance) < 50) return;
      distance > 0 ? this.showPrevious(true) : this.showNext(true);
    }

    handleBlockSelect(event) {
      const selectedIndex = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
      if (selectedIndex >= 0) this.showSlide(selectedIndex, true);
    }

    toggleAutoplay() {
      this.manuallyPaused = !this.manuallyPaused;
      this.autoplayButton.setAttribute('aria-pressed', String(this.manuallyPaused));
      this.autoplayButton.setAttribute('aria-label', this.manuallyPaused ? 'Play carousel' : 'Pause carousel');
      this.autoplayButton.querySelector('[data-tmw-pause-label]').hidden = this.manuallyPaused;
      this.autoplayButton.querySelector('[data-tmw-play-label]').hidden = !this.manuallyPaused;
      this.stopTimer();
      this.pauseAllVideos();
      if (!this.manuallyPaused && this.isVisible) this.startActiveMedia();
    }

    showPrevious(userInitiated = false) {
      this.showSlide((this.currentIndex - 1 + this.slides.length) % this.slides.length, userInitiated);
    }

    showNext(userInitiated = false) {
      this.showSlide((this.currentIndex + 1) % this.slides.length, userInitiated);
    }

    showSlide(index, userInitiated = false) {
      this.stopTimer();
      this.pauseAllVideos();
      this.currentIndex = index;

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', active ? '0' : '-1'));
      });

      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        active ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current');
      });

      if (userInitiated) this.querySelector('.tmw-hero__slides')?.setAttribute('aria-live', 'polite');
      if (this.isVisible) this.startActiveMedia();
    }

    startActiveMedia() {
      if (this.manuallyPaused) return;
      const slide = this.slides[this.currentIndex];
      if (!slide) return;

      const isVideo = slide.dataset.mediaType === 'video';
      const video = isVideo ? this.getActiveVideo(slide) : null;
      if (video) {
        video.muted = true;
        video.playsInline = true;
        video.loop = this.slides.length === 1;
        video.currentTime = 0;
        if (!this.reducedMotion.matches) video.play().catch(() => {});
      }

      if (this.autoplayEnabled && this.slides.length > 1 && !this.reducedMotion.matches) {
        this.timer = window.setTimeout(() => this.showNext(), this.imageDuration);
      }
    }

    getActiveVideo(slide) {
      const desktopVideo = slide.querySelector('[data-tmw-video-desktop] video');
      const mobileVideo = slide.querySelector('[data-tmw-video-mobile] video');
      return this.mobileMedia.matches && mobileVideo ? mobileVideo : desktopVideo;
    }

    pauseAllVideos() {
      this.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.onended = null;
      });
    }

    stopTimer() {
      if (this.timer) window.clearTimeout(this.timer);
      this.timer = null;
    }

    get autoplayEnabled() {
      return this.dataset.autoplay === 'true';
    }

    get imageDuration() {
      return Number(this.dataset.imageDuration) || 6000;
    }
  }

  customElements.define('tmw-hero', TmwHero);
}
